// pages/DisciplineList.js
import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { fetchWithAuth } from '../auth';
import { SkeletonCardGrid } from '../components/ui';
import HeroSearch from '../components/HeroSearch';
import FilterBar from '../components/FilterBar';
import ResourceSearchResults from '../components/ResourceSearchResults';
import UpcomingEventsSection from '../components/UpcomingEventsSection';
import FeaturedResourcesSection from '../components/FeaturedResourcesSection';
import RecentResourcesSection from '../components/RecentResourcesSection';

// Component for horizontal discipline buttons (beneath search)
function HorizontalDisciplineButtons({ disciplines, getDisciplineRoute }) {
  // Get discipline color and icon
  const getDisciplineStyle = (name) => {
    if (name.includes('תושב"ע')) {
      return {
        bgColor: 'bg-teal-500',
        icon: (
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
        )
      };
    }
    if (name.includes('תנ"ך')) {
      return {
        bgColor: 'bg-orange-500',
        icon: (
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        )
      };
    }
    if (name.includes('מחשבת ישראל')) {
      return {
        bgColor: 'bg-purple-500',
        icon: (
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        )
      };
    }
    if (name.includes('רבני בתי ספר')) {
      return {
        bgColor: 'bg-green-500',
        icon: (
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        )
      };
    }
    // Default
    return {
      bgColor: 'bg-gray-500',
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      )
    };
  };

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white dark:from-secondary-900 dark:to-secondary-800 py-6">
      <div className="container-app max-w-5xl mx-auto px-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          {disciplines.map((d) => {
          const route = getDisciplineRoute(d.name);
          const disabled = !d.is_enabled || !route;
            const style = getDisciplineStyle(d.name);

            const ButtonContent = (
              <div className={`
                bg-white dark:bg-secondary-800 rounded-xl shadow-md hover:shadow-lg
                transition-all duration-300 p-4 sm:p-5 flex flex-col items-center gap-3
                  ${disabled
                    ? 'opacity-60 cursor-not-allowed'
                  : 'cursor-pointer hover:scale-105'
                  }
              `}>
                {/* Circular icon container */}
                <div className={`
                  w-12 h-12 sm:w-14 sm:h-14 rounded-full ${style.bgColor} 
                  flex items-center justify-center shadow-sm
                  ${disabled ? '' : 'group-hover:scale-110 transition-transform'}
                `}>
                  <div className="scale-75 sm:scale-100">
                    {style.icon}
                  </div>
                      </div>
                
                {/* Label */}
                <span className="text-center font-semibold text-gray-800 dark:text-white text-xs sm:text-sm leading-tight">
                  {d.name}
                </span>
                    </div>
            );

            if (disabled) {
              return (
                <div key={d.id} className="group">
                  {ButtonContent}
            </div>
              );
            }

            return (
              <Link key={d.id} to={route} className="group">
                {ButtonContent}
              </Link>
          );
        })}
        </div>
      </div>
    </div>
  );
}

export default function DisciplineList() {
  const [disciplines, setDisciplines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ subject: 'all', grade: 'all', type: 'all' });
  const [searchTerm, setSearchTerm] = useState('');

  // Must match ResourceType from backend/files/models.py exactly
  const TYPES = [
    { id: 'all', label: 'כל הסוגים' },
    { id: 'teacher_guide', label: 'מדריכים למורה' },
    { id: 'presentation', label: 'מצגות' },
    { id: 'worksheet', label: 'דפי עבודה' },
    { id: 'quiz', label: 'בחנים' },
    { id: 'test', label: 'מבחנים' },
    { id: 'image', label: 'תמונות' },
    { id: 'video', label: 'סרטונים' },
    { id: 'game', label: 'משחקים' },
    { id: 'podcast', label: 'פודקאסטים' },
    { id: 'study_review', label: 'דפי לימוד וחזרה' },
    { id: 'lesson_plan', label: 'מערכי שיעור' },
    { id: 'sheets_havruta', label: 'לימוד בחברותות' },
    { id: 'sheets_parents', label: 'לימוד הורים וילדים' },
  ];

  const GRADES = [
    { id: 'all', label: 'כל הכיתות' },
    { id: '7', label: 'כיתה ז׳' },
    { id: '8', label: 'כיתה ח׳' },
    { id: '9', label: 'כיתה ט׳' },
  ];

  const SUBJECTS = [
    { id: 'all', label: 'כל המקצועות' },
    { id: 'tosheva', label: 'תושב״ע' },
    { id: 'tanach', label: 'תנ״ך' },
    { id: 'machshevet', label: 'מחשבת ישראל' },
    { id: 'school_rabbis', label: 'רבני בתי ספר' },
  ];

  // Filter disciplines based on search and filters
  const filteredDisciplines = useMemo(() => {
    let filtered = disciplines;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(d => 
        d.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by subject
    if (filters.subject !== 'all') {
      const subjectMap = {
        'tosheva': 'תושב"ע',
        'tanach': 'תנ"ך',
        'machshevet': 'מחשבת ישראל',
        'school_rabbis': 'רבני בתי ספר',
      };
      const subjectName = subjectMap[filters.subject];
      if (subjectName) {
        filtered = filtered.filter(d => d.name.includes(subjectName));
      }
    }

    return filtered;
  }, [disciplines, searchTerm, filters]);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await fetchWithAuth('/api/disciplines/');
        if (!res.ok) throw new Error('Failed to load disciplines');
        const data = await res.json();
        // Backend already filters by is_enabled, so no need to filter again
        if (!ignore) setDisciplines(data);
      } catch {
        if (!ignore) setError('שגיאה בטעינת התחומי דעת');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, []);

  const getDisciplineRoute = (disciplineName) => {
    if (disciplineName.includes('תושב"ע')) return '/toshba/classes';
    if (disciplineName.includes('מחשבת ישראל')) return '/jewish-thought/subtopics';
    if (disciplineName.includes('תנ"ך')) return '/tanach/books';
    if (disciplineName.includes('רבני בתי ספר')) return '/school-rabbis/main-topics';
    return null;
  };

  return (
    <>
      {/* Hero Search Section */}
      <HeroSearch 
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        activeFilters={filters}
        setActiveFilters={setFilters}
        showFilters={false}
        useVideoBackground={true}
      />

      {/* Horizontal Discipline Buttons */}
      {!loading && !error && disciplines.length > 0 && (
        <HorizontalDisciplineButtons 
          disciplines={disciplines} 
          getDisciplineRoute={getDisciplineRoute} 
        />
      )}

    <div className="page-wrapper">
        {/* Secondary Filters Bar */}
        <FilterBar
          filters={filters}
          setFilters={setFilters}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
        />

        {/* Resource Search Results */}
        <ResourceSearchResults filters={filters} />

        {/* Section 1: Upcoming Events */}
        <UpcomingEventsSection />

        {/* Section 2: Recent Resources */}
        <RecentResourcesSection />

        {/* Section 3: Featured Resources (4 rows) */}
        <FeaturedResourcesSection />

      {loading && <SkeletonCardGrid count={4} />}

      {error && (
        <div className="text-center py-8">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {!loading && !error && filteredDisciplines.length === 0 && searchTerm && (
              <div className="text-center py-20 bg-white dark:bg-secondary-800 rounded-xl border border-dashed border-gray-300 dark:border-secondary-600">
                <p className="text-xl text-gray-500 dark:text-gray-400">לא נמצאו תחומי דעת התואמים את החיפוש שלך.</p>
                <button 
                  onClick={() => {
                    setFilters({subject:'all', grade:'all', type:'all'});
                    setSearchTerm('');
                  }}
                  className="mt-4 text-primary-600 dark:text-primary-400 font-bold hover:underline"
                >
                  נסה לנקות סינונים
                </button>
              </div>
      )}

      {!loading && !error && disciplines.length === 0 && (
        <p className="text-center text-secondary-500 dark:text-secondary-400 py-8">
          אין תחומי דעת להצגה עדיין.
        </p>
      )}
    </div>
    </>
  );
}